import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { FiArrowRight, FiPlusCircle, FiClock, FiArrowDownCircle, FiArrowUpCircle, FiCopy, FiCheck, FiAlertCircle, FiRefreshCw, FiX, FiShield, FiCreditCard, FiSend, FiChevronLeft } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

export default function WalletScreen() {
  const { user, userType } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(true);
  const [codeCopied, setCodeCopied] = useState(false);

  // States for Top Up Modal
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState('choice'); // 'choice' | 'chargily'
  const [topUpAmount, setTopUpAmount] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // States for Send Money Modal
  const [showSendMoneyModal, setShowSendMoneyModal] = useState(false);
  const [sendReceiverCode, setSendReceiverCode] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [isProcessingSend, setIsProcessingSend] = useState(false);

  const isProvider = userType === 'provider';

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    try {
      let profileData = null;
      if (isProvider) {
        profileData = await supabaseService.getProviderById(user.id);
      } else {
        profileData = await supabaseService.getClientById(user.id);
      }
      setProfile(profileData);
      await loadTransactions();
    } catch (error) {
      console.error('Error loading wallet:', error);
      toast.error('فشل تحميل بيانات المحفظة');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    if (!user) return;
    setTxLoading(true);
    try {
      const data = await supabaseService.getWalletTransactions(user.id);
      const filtered = (data || []).filter(t => t.type !== 'frozen');
      setTransactions(filtered);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setTxLoading(false);
    }
  };

  const totalBalance = profile?.wallet ?? profile?.wallet_balance ?? profile?.walletBalance ?? profile?.availableBalance ?? 0;
  const frozenBalance = !isProvider ? (profile?.walletFrozen ?? profile?.wallet_frozen ?? 0) : 0;
  const availableBalance = totalBalance - frozenBalance;
  const walletCode = profile?.walletCode || profile?.wallet_code || '------';

  const totalCredit = transactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.amount, 0);
  const totalDebit = transactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.amount, 0);

  const copyCode = () => {
    navigator.clipboard.writeText(walletCode);
    setCodeCopied(true);
    toast.success('تم نسخ الكود');
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const openTopUp = () => {
    setPaymentStep('choice');
    setTopUpAmount('');
    setShowTopUpModal(true);
  };
  const closeTopUp = () => setShowTopUpModal(false);
  const handleAmountClick = (amount) => setTopUpAmount(amount.toString());

  const chooseChargily = () => setPaymentStep('chargily');
  const chooseBaridiMob = () => {
    setShowTopUpModal(false);
    navigate('/wallet/recharge-receipt');
  };

  // ══════════════════════════════════════════════════════════
  //  Logic: Chargily Payment via Supabase Function
  // ══════════════════════════════════════════════════════════
  const processPayment = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount < 100) {
      toast.error('أدخل مبلغاً صحيحاً (100 دج على الأقل)');
      return;
    }

    setIsProcessingPayment(true);
    toast.loading('جاري إنشاء رابط الدفع...', { id: 'chargily-loading' });

    try {
      // ✅ استدعاء Supabase Edge Function التي أنشأناها
      const { data, error } = await supabaseService.client.functions.invoke('create-chargily-checkout', {
        body: {
          amount: amount,
          user_id: user?.id,
          is_provider: isProvider
        }
      });

      toast.dismiss('chargily-loading');

      if (error) {
        // معالجة الأخطاء القادمة من الـ Function
        console.error('Function Error:', error);
        throw new Error(error.message || 'فشل الاتصال بالخادم');
      }

      if (data?.checkout_url) {
        toast.success('جاري تحويلك لبوابة الدفع...');
        // توجيه المستخدم لصفحة الدفع
        window.location.href = data.checkout_url;
      } else {
        throw new Error('لم يتم استلام رابط الدفع من البوابة');
      }

    } catch (error) {
      toast.dismiss('chargily-loading');
      console.error('Payment Error:', error);
      toast.error(error.message || 'حدث خطأ غير متوقع');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const openHistory = () => navigate('/wallet/transactions');
  const openWithdraw = () => navigate('/wallet/withdraw');

  const handleSendMoney = async () => {
    const amount = parseFloat(sendAmount);
    const code = sendReceiverCode.trim();

    if (!code) {
      toast.error('الرجاء إدخال كود المحفظة للمستلم');
      return;
    }
    if (!amount || amount <= 0) {
      toast.error('الرجاء إدخال مبلغ صحيح أكبر من 0');
      return;
    }
    if (amount > availableBalance) {
      toast.error('رصيدك المتاح غير كافٍ لإجراء هذه العملية');
      return;
    }
    if (code === walletCode) {
      toast.error('لا يمكنك التحويل لنفسك');
      return;
    }

    setIsProcessingSend(true);
    toast.loading('جاري تحويل الأموال...', { id: 'send-money-loading' });

    try {
      await supabaseService.transferFunds(user.id, code, amount);
      toast.dismiss('send-money-loading');
      toast.success('تم تحويل الأموال بنجاح!');
      setShowSendMoneyModal(false);
      setSendReceiverCode('');
      setSendAmount('');
      await loadData();
    } catch (error) {
      toast.dismiss('send-money-loading');
      console.error('Send money error:', error);
      let errorMsg = 'فشل عملية التحويل، يرجى التحقق من الكود أو المحاولة لاحقاً';
      if (error && error.message) {
        if (error.message.includes('sender_not_found')) {
          errorMsg = 'لم يتم العثور على حساب المرسل';
        } else if (error.message.includes('insufficient_balance')) {
          errorMsg = 'رصيدك غير كافٍ لإجراء التحويل';
        } else if (error.message.includes('receiver_not_found')) {
          errorMsg = 'كود المحفظة غير صحيح أو غير مسجل لدينا';
        } else if (error.message.includes('cannot_transfer_to_self')) {
          errorMsg = 'لا يمكنك التحويل إلى كود محفظتك الخاص';
        } else {
          errorMsg = `خطأ: ${error.message}`;
        }
      }
      toast.error(errorMsg);
    } finally {
      setIsProcessingSend(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black"></div></div>;

  return (
    <div className="h-screen w-full bg-gray-50 font-sans flex flex-col overflow-hidden" dir="rtl">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 lg:px-10 py-3 flex items-center gap-3 shadow-sm shrink-0">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
          <FiArrowRight className="text-gray-700" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white font-bold text-xs">K</div>
          <h1 className="text-lg font-bold text-gray-900">محفظتي</h1>
        </div>
      </div>

      {/* Main content — fills remaining height, no page scroll */}
      <div className="flex-1 min-h-0 px-6 lg:px-10 py-5 lg:py-8">
        <div className="h-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">

          {/* ── العمود الأيمن: الرصيد + الإجراءات (يمرر داخليًا عند الحاجة فقط) ── */}
          <div className="lg:col-span-2 flex flex-col gap-4 min-h-0 overflow-y-auto pr-1">

            {/* بطاقة الرصيد */}
            <div className="w-full shrink-0 rounded-3xl shadow-2xl relative overflow-hidden p-5 text-white flex flex-col gap-4"
                 style={{ background: 'linear-gradient(135deg, #111111 0%, #1a1a1a 50%, #222222 100%)' }}>

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center border border-white/20">
                    <span className="font-bold text-sm">K</span>
                  </div>
                  <div>
                    <h2 className="text-sm font-bold tracking-widest opacity-90">KINPAY WALLET</h2>
                    <p className="text-[10px] text-gray-400 tracking-wider">{isProvider ? 'PROVIDER' : 'CLIENT'}</p>
                  </div>
                </div>
                <div className="px-3 py-1 rounded-full bg-green-500/20 border border-green-500/40 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                  <span className="text-[11px] font-bold text-green-400">نشطة</span>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-400 mb-1">الرصيد المتاح</p>
                <div className="flex items-end gap-2 mb-2">
                  <h1 className="text-4xl font-extrabold tracking-tighter">{availableBalance.toFixed(2)}</h1>
                  <span className="text-base text-gray-300 mb-1">دج</span>
                </div>

                {!isProvider && frozenBalance > 0 && (
                  <div className="mt-1 px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center gap-2 w-fit">
                    <FiAlertCircle className="text-orange-400" size={14} />
                    <span className="text-xs text-orange-300 font-medium">{frozenBalance.toFixed(0)} دج مجمّد</span>
                  </div>
                )}
              </div>

              <div>
                <div className="border-t border-white/10 mb-3"></div>

                <div className="flex justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <FiArrowDownCircle className="text-green-400" size={18} />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">الواردات</p>
                      <p className="text-sm font-bold">+{totalCredit.toFixed(0)} دج</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <FiArrowUpCircle className="text-red-400" size={18} />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">الصادرات</p>
                      <p className="text-sm font-bold">-{totalDebit.toFixed(0)} دج</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* أزرار الإجراءات */}
            <div className="grid grid-cols-3 gap-3 shrink-0">
              <button onClick={openTopUp} className="col-span-1 bg-blue-600 text-white p-3.5 rounded-2xl flex flex-col items-center gap-1.5 shadow-lg shadow-blue-600/30 active:scale-95 transition">
                <FiPlusCircle size={20} />
                <span className="text-xs sm:text-sm font-bold">شحن</span>
              </button>
              <button onClick={() => setShowSendMoneyModal(true)} className="col-span-1 bg-purple-600 text-white p-3.5 rounded-2xl flex flex-col items-center gap-1.5 shadow-lg shadow-purple-600/30 active:scale-95 transition">
                <FiSend size={20} />
                <span className="text-xs sm:text-sm font-bold">إرسال مال</span>
              </button>
              <button onClick={openHistory} className="col-span-1 bg-white border border-gray-200 text-gray-800 p-3.5 rounded-2xl flex flex-col items-center gap-1.5 active:scale-95 transition">
                <FiClock size={20} />
                <span className="text-xs sm:text-sm font-bold">السجل</span>
              </button>
              {isProvider && (
                <button onClick={openWithdraw} className="col-span-3 bg-green-500/10 border border-green-500/30 text-green-600 p-3.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition">
                  <FiArrowUpCircle size={20} />
                  <span className="text-sm font-bold">طلب سحب الأرباح</span>
                </button>
              )}
            </div>

            {/* كود المحفظة */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 shrink-0">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">#</span>
                  <h3 className="font-bold text-gray-800">كود محفظتك</h3>
                </div>
                <button onClick={copyCode} className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${codeCopied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {codeCopied ? 'تم!' : 'نسخ'}
                </button>
              </div>
              <div className="flex justify-between gap-2" dir="ltr">
                {walletCode.split('').map((char, i) => (
                  <div key={i} className="flex-1 h-11 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center text-lg font-mono font-bold text-gray-800">
                    {char}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">شارك هذا الكود لاستلام التحويلات.</p>
            </div>
          </div>

          {/* ── العمود الأيسر: سجل المعاملات ── */}
          <div className="lg:col-span-3 flex flex-col min-h-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0">
              <div className="p-4 flex justify-between items-center border-b border-gray-100 shrink-0">
                <h3 className="font-bold text-gray-800">سجل المعاملات</h3>
                <button onClick={loadTransactions} className="text-gray-400 hover:text-gray-600">
                  <FiRefreshCw className={txLoading ? 'animate-spin' : ''} />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                {txLoading ? (
                  <div className="p-8 text-center text-gray-400">جاري التحميل...</div>
                ) : transactions.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <FiClock className="mx-auto mb-2" size={24} />
                    <p className="text-sm">لا توجد معاملات بعد</p>
                  </div>
                ) : (
                  <div>
                    {transactions.map((tx, i) => (
                      <div key={tx.id || i} className="p-4 flex items-center justify-between border-b border-gray-50 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.type === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            {tx.type === 'credit' ? <FiArrowDownCircle size={20} /> : <FiArrowUpCircle size={20} />}
                          </div>
                          <div>
                            <p className="font-bold text-sm text-gray-800">{tx.type === 'credit' ? 'وارد' : 'صادر'}</p>
                            <p className="text-xs text-gray-400">{tx.note || 'معاملة'}</p>
                          </div>
                        </div>
                        <div className="text-left">
                          <p className={`font-bold text-sm ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.type === 'credit' ? '+' : '-'}{tx.amount.toFixed(0)} دج
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {new Date(tx.created_at || tx.createdAt).toLocaleDateString('ar-DZ')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ═══════════════ Modal: شحن الرصيد ═══════════════ */}
      {showTopUpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={closeTopUp}>
          <div
            className="bg-white w-full max-w-lg rounded-3xl p-6 mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── الخطوة 1: اختيار طريقة الشحن ── */}
            {paymentStep === 'choice' && (
              <>
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <FiPlusCircle className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">شحن الرصيد</h2>
                      <p className="text-xs text-gray-500">اختر طريقة الشحن المناسبة لك</p>
                    </div>
                  </div>
                  <button onClick={closeTopUp} className="p-2 rounded-full bg-gray-100">
                    <FiX size={18} />
                  </button>
                </div>

                <div className="space-y-3">
                  {/* خيار Chargily Pay */}
                  <button
                    onClick={chooseChargily}
                    className="w-full p-4 rounded-2xl border-2 border-gray-200 hover:border-blue-500 flex items-center gap-4 transition-all active:scale-[0.98]"
                  >
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                      <FiCreditCard className="text-blue-600" size={22} />
                    </div>
                    <div className="flex-1 text-right">
                      <h3 className="font-bold text-gray-900 text-sm">الدفع الإلكتروني</h3>
                      <p className="text-xs text-gray-500">عبر EDAHABIA أو CIB — Chargily Pay</p>
                    </div>
                    <FiChevronLeft className="text-gray-400" />
                  </button>

                  {/* خيار بريدي موب */}
                  <button
                    onClick={chooseBaridiMob}
                    className="w-full p-4 rounded-2xl border-2 border-gray-200 hover:border-green-500 flex items-center gap-4 transition-all active:scale-[0.98]"
                  >
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                      <FiSend className="text-green-600" size={22} />
                    </div>
                    <div className="flex-1 text-right">
                      <h3 className="font-bold text-gray-900 text-sm">تحويل بريدي موب</h3>
                      <p className="text-xs text-gray-500">حوّل ثم ارفع صورة الوصل للمراجعة</p>
                    </div>
                    <FiChevronLeft className="text-gray-400" />
                  </button>
                </div>
              </>
            )}

            {/* ── الخطوة 2: نموذج Chargily (نفس القديم) ── */}
            {paymentStep === 'chargily' && (
              <>
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setPaymentStep('choice')} className="p-2 rounded-full bg-gray-100">
                      <FiArrowRight className="text-gray-700" size={16} />
                    </button>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">شحن الرصيد</h2>
                      <p className="text-xs text-gray-500">عبر EDAHABIA أو CIB</p>
                    </div>
                  </div>
                  <button onClick={closeTopUp} className="p-2 rounded-full bg-gray-100">
                    <FiX size={18} />
                  </button>
                </div>

                <div className="mb-4">
                  <label className="text-xs text-gray-600 mb-1 block">المبلغ بالدينار الجزائري (دج)</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={topUpAmount}
                      onChange={(e) => setTopUpAmount(e.target.value)}
                      placeholder="ادخل المبلغ"
                      className="w-full p-4 pr-12 border-2 border-gray-200 rounded-xl text-2xl font-bold focus:border-blue-500 outline-none"
                    />
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">دج</span>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-6">
                  {[500, 1000, 2000, 5000].map(amt => (
                    <button
                      key={amt}
                      onClick={() => handleAmountClick(amt)}
                      className={`py-2 rounded-lg text-sm font-bold transition ${topUpAmount === amt.toString() ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                      {amt}
                    </button>
                  ))}
                </div>

                <div className="bg-blue-50 p-3 rounded-lg flex items-start gap-3 mb-6 text-xs text-blue-800">
                  <FiShield className="text-blue-400 mt-0.5" size={16} />
                  <span>الدفع يتم بأمان عبر بوابة Chargily Pay بالبطاقة البريدية (EDAHABIA) أو CIB.</span>
                </div>

                <button
                  onClick={processPayment}
                  disabled={isProcessingPayment}
                  className="w-full py-4 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-2 transition-all bg-gradient-to-r from-blue-600 to-blue-700 hover:opacity-90 disabled:opacity-50"
                >
                  {isProcessingPayment ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'الدفع عبر Chargily Pay'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ Modal: إرسال المال ═══════════════ */}
      {showSendMoneyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowSendMoneyModal(false)}>
          <div
            className="bg-white w-full max-w-lg rounded-3xl p-6 mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <FiSend className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">إرسال مال</h2>
                  <p className="text-xs text-gray-500">حوّل رصيداً إلى شخص آخر فوراً</p>
                </div>
              </div>
              <button onClick={() => setShowSendMoneyModal(false)} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                <FiX size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">كود محفظة المستلم</label>
                <input
                  type="text"
                  value={sendReceiverCode}
                  onChange={(e) => setSendReceiverCode(e.target.value)}
                  placeholder="مثال: XXXXXX"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl font-mono text-center font-bold tracking-widest focus:border-purple-500 outline-none uppercase"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600 mb-1 block">المبلغ بالدينار الجزائري (دج)</label>
                <div className="relative">
                  <input
                    type="number"
                    value={sendAmount}
                    onChange={(e) => setSendAmount(e.target.value)}
                    placeholder="ادخل المبلغ"
                    className="w-full p-4 pr-12 border-2 border-gray-200 rounded-xl text-2xl font-bold focus:border-purple-500 outline-none"
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">دج</span>
                </div>
              </div>

              <div className="bg-purple-50 p-3.5 rounded-xl flex items-start gap-3 text-xs text-purple-800">
                <FiShield className="text-purple-400 mt-0.5 shrink-0" size={16} />
                <span>الرجاء التأكد من كود محفظة الشخص الآخر قبل الإرسال. عمليات التحويل نهائية ولا يمكن التراجع عنها.</span>
              </div>

              <button
                onClick={handleSendMoney}
                disabled={isProcessingSend}
                className="w-full py-4 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-2 transition-all bg-gradient-to-r from-purple-600 to-purple-700 hover:opacity-90 disabled:opacity-50"
              >
                {isProcessingSend ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'تأكيد وإرسال المال'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}